/**
 * Centralized initialization operations for the application
 * This handles all initial data fetching that should occur when the app boots up
 */

import { getUserRolesOperation } from 'src/routes/users-and-permissions/roles/roles.operations';
import { getUserAgenciesOperation } from 'src/store/user/user.operations';
import { syncFeatureFlagsOperation } from 'src/store/feature-flags/feature-flags.operations';
import { getAgencyRoutesOperation, getRoutesStopsOperation } from 'src/routes/routes/routes.operations';
import { identifyUser } from 'src/utils/analytics';
import { isTrafficViewerRole } from 'src/constants/roles.constants';

/**
 * Build the non-PII user properties payload that gets sent to GA4 and Clarity.
 * Centralized so every call site (init, agency switch, jurisdiction switch)
 * produces the same shape.
 *
 * Unified schema — every property is registered once in GA4, roles that don't
 * apply simply emit empty strings (which GA4 filters out on the set call in
 * `analytics.setGaUserProperties`). Property meanings:
 *
 *   user_role             Backend usertype string.
 *   lyt_employee          'true'|'false' — set for everyone (derived from email domain).
 *   agency_id             Current agency context for all roles. Dynamic for
 *                         LYT users (sidebar switcher) and traffic viewers
 *                         (inferred from selected jurisdiction); static for
 *                         agency users.
 *   agency_type           agency_type of agency_id.
 *   jurisdiction_id       Current jurisdiction — traffic viewers only.
 *   jurisdiction_name     Human-readable jurisdiction name — traffic viewers only.
 *   assigned_agency_count Number of agencies the user has access to. Only set
 *                         for traffic viewers (the only role that can span
 *                         multiple agencies); useful for segmenting
 *                         single- vs multi-agency viewers.
 */
export const buildUserAnalyticsProperties = (state) => {
    const personalInfo = state.user?.personalInformation || {};
    const agencyId = state.user?.agencyId || state.user?.selectedLocation?.agencyId || '';
    const agencyList = state.userInfo?.agencies?.list || [];
    const agency = agencyList.find((a) => a?.agencies_id === agencyId);
    const email = personalInfo.email || '';
    const isLytEmployee = email.toLowerCase().endsWith('@lyt.ai');

    const usertype = personalInfo.usertype;
    const isTrafficViewer = isTrafficViewerRole(usertype);

    const jurisdictionId = isTrafficViewer
        ? (state.userInfo?.selectedJurisdiction || state.user?.selectedLocation?.cityId || '')
        : '';
    const jurisdictionNameMap = state.userInfo?.jurisdictionNameMap || {};
    const jurisdictionName = jurisdictionId ? (jurisdictionNameMap[jurisdictionId] || '') : '';

    const assignedAgencyCount = isTrafficViewer ? String(agencyList.length || 0) : '';

    return {
        user_role: usertype,
        agency_id: agencyId,
        agency_type: agency?.agency_type,
        jurisdiction_id: jurisdictionId,
        jurisdiction_name: jurisdictionName,
        assigned_agency_count: assignedAgencyCount,
        lyt_employee: isLytEmployee ? 'true' : 'false',
    };
};

/**
 * Identify the current user across analytics tools (GA4 + Clarity). Called
 * from initializeApplication once user data is loaded, so analytics identity
 * is never coupled to any particular UI component being mounted.
 */
const identifyUserForAnalytics = (state) => {
    const personalInfo = state.user?.personalInformation || {};
    const userId = personalInfo.email || personalInfo.user_id;
    if (!userId) return;

    identifyUser(userId, buildUserAnalyticsProperties(state));
};

/**
 * Main initialization thunk that runs when the app boots up for authenticated users
 * This centralizes all initial data fetching to avoid scattered useEffect calls
 * 
 * @param {Object} options - Initialization options
 * @param {boolean} options.skipUserData - Skip user-specific data (for public routes)
 * @param {boolean} options.skipRoles - Skip role fetching (if already loaded)
 * @returns {Function} Redux thunk
 */
export const initializeApplication = (options = {}) => async (dispatch, getState) => {
    const {
        skipUserData = false,
        skipRoles = false,
        forceUserAgencies = false,
    } = options;

    try {
        // Get current state to check what's already loaded
        const state = getState();
        const userRoles = state.usersAndPermissions?.roles?.userRoles;
        const userAgencies = state.userInfo?.agencies;

        // Array to track all initialization promises
        const initPromises = [];

        // 1. Fetch user roles if not already loaded and not skipped
        if (!skipRoles && (!userRoles?.list || userRoles.list.length === 0) && !userRoles?.loading) {
            initPromises.push(
                dispatch(getUserRolesOperation())
                    .catch((error) => {
                        console.warn('Failed to load user roles during initialization:', error);
                        // Don't fail the entire initialization for roles
                        return Promise.resolve();
                    })
            );
        }

        // 2. Fetch user agencies if not already loaded and not skipping user data
        //    forceUserAgencies bypasses the guard (used after fresh login when
        //    usertype was not yet available during an earlier attempt).
        const needsUserAgencies = forceUserAgencies || (!userAgencies?.list || userAgencies.list.length === 0) && !userAgencies?.loading;
        if (!skipUserData && needsUserAgencies) {
            initPromises.push(
                dispatch(getUserAgenciesOperation())
                    .catch((error) => {
                        console.warn('Failed to load user agencies during initialization:', error);
                        // Don't fail the entire initialization for user agencies
                        return Promise.resolve();
                    })
            );
        }

        // Wait for all initialization promises to complete
        if (initPromises.length > 0) {
            await Promise.allSettled(initPromises);
        }

        // 3. Fetch agency routes and stops (depends on agency being set by step 2)
        if (!skipUserData) {
            const postAgencyState = getState();

            // Identify the user for analytics now that user + agency data is
            // loaded. Runs once per init; agency switches call
            // updateUserProperties separately to keep user_properties fresh.
            identifyUserForAnalytics(postAgencyState);

            // First LD sync runs after user + agency context is available so
            // targeting attributes (agency_id, role, etc.) are complete.
            await dispatch(syncFeatureFlagsOperation());

            const agencyId = getState().user?.selectedLocation?.agencyId;
            if (agencyId) {
                await Promise.allSettled([
                    dispatch(getAgencyRoutesOperation())
                        .catch((error) => {
                            console.warn('Failed to load agency routes during initialization:', error);
                            return Promise.resolve();
                        }),
                    dispatch(getRoutesStopsOperation({}))
                        .catch((error) => {
                            console.warn('Failed to load routes stops during initialization:', error);
                            return Promise.resolve();
                        }),
                ]);
            }
        } else {
            const flags = getState().featureFlags;
            if (!flags?.ready && !flags?.loading) {
                await dispatch(syncFeatureFlagsOperation());
            }
        }

        return {
            success: true,
            tasksRun: initPromises.length,
        };

    } catch (error) {
        console.error('Critical error during initialization:', error);
        
        // Even if there's a critical error, don't fail the app completely
        // Log the error and continue
        return {
            success: false,
            error: error.message,
            tasksRun: 0,
        };
    }
};

/**
 * Quick initialization for already authenticated users (e.g. Auth0 silent
 * re-auth on direct navigation, where access + accessToken are present but
 * the agencies/roles slices are empty).
 *
 * Loads roles and agencies. Same shape as the fresh-login path —
 * personalInformation is fetched separately and triggers the bootstrap chain.
 *
 * @returns {Function} Redux thunk
 */
export const initializeForExistingSession = () => async (dispatch, getState) => {
    return dispatch(initializeApplication({
        skipUserData: false,
        skipRoles: false,
    }));
};

/**
 * Initialization for fresh login.
 * This runs after getUserPersonalInformation() resolves, so usertype is
 * now available in state.  We force-fetch agencies because the user-role
 * branch inside getUserAgenciesOperation() needs a populated usertype.
 * 
 * @returns {Function} Redux thunk
 */
export const initializeAfterLogin = () => async (dispatch, getState) => {
    return dispatch(initializeApplication({
        skipUserData: false,
        skipRoles: false,
        forceUserAgencies: true, // usertype was empty before, re-fetch with correct role
    }));
};

/**
 * Minimal initialization for public routes
 * Only fetches data that's needed for unauthenticated users
 * 
 * @returns {Function} Redux thunk
 */
export const initializeForPublicRoutes = () => async (dispatch, getState) => {
    return dispatch(initializeApplication({
        skipUserData: true,  // No user-specific data needed
        skipRoles: true,     // No roles needed for public routes
    }));
};

/**
 * Re-initialization helper for when critical data needs to be refreshed
 * Useful for when user permissions change or data becomes stale
 * 
 * @param {Object} options - What to force refresh
 * @returns {Function} Redux thunk
 */
export const reinitializeApplication = (options = {}) => async (dispatch, getState) => {
    const {
        forceRoles = false,
        forceAgencies = false,
        forceUserAgencies = false,
    } = options;

    // If forcing refresh, we need to clear the loading states and data first
    // This ensures the initialization logic will run even if data exists

    const initOptions = {
        skipUserData: false,
        skipRoles: !forceRoles,
    };

    return dispatch(initializeApplication(initOptions));
};
